import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import type {ProductOverviewSliceGridWithIcons} from 'prismicio-types'
import {Container} from 'src/common/Container'
import {Gradient} from './components/separators'

type IconsVariationProps = {
  slice: ProductOverviewSliceGridWithIcons & {
    slice_type: string
    variation: string
  }
}

export const ProductOverviewIconsVariation = ({slice}: IconsVariationProps) => (
  <Container
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    className="flex flex-col items-center gap-3 !pb-0 !pt-10 md:gap-6 md:!pt-12 lg:!pt-20"
    backgroundColor={slice.primary.background_color || 'Moss White'}
  >
    <div
      className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 px-5 md:gap-6 md:px-10 lg:px-5 xl:px-0"
      data-aos="fade-up"
      data-aos-offset="0"
    >
      <PrismicRichText
        field={slice.primary.tag}
        components={{
          paragraph: ({children}) => (
            <p className="text-button-s md:text-button-s font-semibold uppercase text-moss-blue">{children}</p>
          ),
          strong: ({children}) => <strong className="pr-3 font-semibold text-moss-blue">{children}</strong>,
        }}
      />
      <div>
        <PrismicRichText
          field={slice.primary.heading}
          components={{
            heading1: ({children}) => (
              <h2 className="md:text-tg-h2 text-[32px] font-normal leading-[normal] tracking-[-.32px] text-moss-black">
                {children}
              </h2>
            ),
            strong: ({children}) => <span className="text-moss-grey-400">{children}</span>,
          }}
        />
      </div>
    </div>
    <div
      data-aos="fade-up"
      data-aos-offset="0"
      className="
          relative
          isolate
          grid
          w-full
          grid-cols-[20px_1fr_20px]
          grid-rows-[32px_repeat(6,auto)_32px]
          md:grid-cols-[40px_minmax(341px,auto)_minmax(341px,auto)_40px]
          md:grid-rows-[64px_repeat(3,auto)_64px]
          lg:grid-cols-[20px_minmax(0,401px)_minmax(0,401px)_minmax(0,401px)_20px]
          lg:grid-rows-[64px_repeat(2,218px)_64px]
          xl:grid-cols-[1fr_minmax(0,401px)_minmax(0,401px)_minmax(0,401px)_1fr]
          "
    >
      <Gradient color="255, 255, 255" gradientStart={0} height={24} location="top" />
      {slice.primary.grid_elements.map((content, index) => (
        <div
          key={`icon-${index}`}
          className="col-span-1 ml-[-1px] mt-[-1px] flex items-center justify-center rounded-[4px] border border-moss-grey-150 p-6 md:p-8 lg:justify-items-start lg:px-4 lg:py-10"
        >
          <div className="flex max-w-[255px] flex-col items-center justify-center gap-3 lg:max-w-[290px]">
            <div className="relative flex size-8 items-center justify-center rounded-[4px] bg-green-neon">
              <PrismicNextImage field={content.icon} alt="" className="size-5" />
              <div className="absolute inset-0 rounded-[4px] border border-black/[10%]" />
            </div>
            <h4 className="text-tg-h3 text-center text-moss-black">{content.title}</h4>
            <p className="text-tg-body-m text-center text-moss-black/[60%]">{content.description}</p>
          </div>
        </div>
      ))}
      <CentralSeparators />
      <SideSeparators />
      <Gradient color="255, 255, 255" gradientStart={20} height={32} location="bottom" />
    </div>
  </Container>
)

const SideSeparators = () => (
  <>
    {/* Left Side Separators */}
    <div className="col-start-1 row-start-1 rounded-br-[4px] border-b border-r border-moss-grey-150" />
    <div className="col-start-1 row-start-2 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150" />
    <div className="col-start-1 row-start-3 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150" />
    <div className="col-start-1 row-start-4 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 lg:hidden" />
    <div className="col-start-1 row-start-5 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 md:hidden" />
    <div className="col-start-1 row-start-6 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 md:hidden" />
    <div className="col-start-1 row-start-7 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 md:hidden" />
    <div className="col-start-1 row-start-8 mt-[-1px] rounded-tr-[4px] border-r border-t border-moss-grey-150 md:row-start-5 lg:row-start-4" />

    {/* Right Side Separators */}
    <div className="col-start-3 row-start-1 ml-[-1px] rounded-bl-[4px] border-b border-l border-moss-grey-150 md:col-start-4 lg:col-start-5" />
    <div className="col-start-3 row-start-2 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:col-start-4 lg:col-start-5" />
    <div className="col-start-3 row-start-3 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:col-start-4 lg:col-start-5" />
    <div className="col-start-3 row-start-4 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:col-start-4 lg:hidden" />
    <div className="col-start-3 row-start-5 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:hidden" />
    <div className="col-start-3 row-start-6 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:hidden" />
    <div className="col-start-3 row-start-7 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:hidden" />
    <div className="col-start-3 row-start-8 ml-[-1px] mt-[-1px] rounded-tl-[4px] border-l border-t border-moss-grey-150 md:col-start-4 md:row-start-5 lg:col-start-5 lg:row-start-4" />
  </>
)

const CentralSeparators = () => (
  <>
    <div className="col-start-2 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150" />
    <div className="col-start-3 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-md:hidden" />
    <div className="col-start-4 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-lg:hidden" />
    <div className="col-start-2 row-start-8 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 md:row-start-5 lg:row-start-4" />
    <div className="col-start-3 row-start-8 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 max-md:hidden md:row-start-5 lg:row-start-4" />
    <div className="col-start-4 row-start-8 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 max-lg:hidden md:row-start-5 lg:row-start-4" />
  </>
)
